import React, { useState } from 'react';
import { space } from 'styled-system';
import styled from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useTranslation from 'hooks/use-translation';
import useUI from 'hooks/use-ui';
import useColors from 'hooks/use-colors';
import { useUpdateUser } from 'src/state/actions/users';
import useErnie from 'shared/hooks/use-ernie';
import { FIELD_NAMES, ERNIE_TYPES } from 'shared/constants';
import { callNativeMethod, changeNativeTabToShop } from 'src/mobile-ecomm/hooks/use-mobile-native-bridge';
import { useModals, ModalNames } from 'components/modals';
import { userFormSchema } from 'components/forms/form-utils';
import { DeleteAccountLink } from 'src/mobile-ecomm/account/account-delete/account-delete';
import { FormTextInput, FormFormattedInput } from 'components/form-inputs';
import Form from 'components/forms/form';
import { HorizontalDivider, TextButton } from 'src/components/core';
import { MobileEcommTermsLink } from 'src/mobile-ecomm/components/terms-link';
import { Typography } from 'src/typography';
import { useUnenrollPayByBankUser } from 'src/payments/hooks';
import { useDeleteAccountModal } from 'components/modals/delete-account/delete-account-modal-controller';
import { ChangePassword, Heading, InputRow, StyledButton } from '../components';

export const SAVE_ERROR_MESSAGE = 'We were unable to save your information. Please try again later.';
export const SAVE_SUCCESS_MESSAGE = 'User information successfully updated!';

export default function MyAccount({ user }) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const updateUser = useUpdateUser();
  const showErnie = useErnie();
  const { isEnrolledDutchiePay, handleUnenrollPayByBank } = useUnenrollPayByBankUser();
  const UI = useUI();
  const { isMobileEcommApp } = UI;
  const appRevision = `7.10.0 (1)`; // mocked till it's passed in from the mobile app
  const colors = useColors();
  const { openModal } = useModals();
  const flags = useFlags();
  const isEmailCodeLoginEnabled = flags['growth.ecomm.email-code-login.rollout'] ?? false;
  const isLoggedInViaExternalProvider = user?.isSsoUser;

  // Users with email code login don't have passwords
  const hasPassword = !isLoggedInViaExternalProvider && !isEmailCodeLoginEnabled;

  const { open: openDeleteAccountModal } = useDeleteAccountModal();

  const defaultValues = {
    firstName: user?.profile?.firstName || '',
    lastName: user?.profile?.lastName || '',
    phone: user?.profile?.phone || '',
    email: (user?.emails && user?.emails.length > 0 && user?.emails[0]?.address) || '',

    // v2 birthdays were stored as m/d/yyyy rather than mm/dd/yyyy so we need to handle them on initial load
    birthday: user?.profile?.birthday?.replace(/\b(\d{1})\b/g, '0$1') || '',
  };

  const formValidationFields = {
    birthday: true,
    email: !isLoggedInViaExternalProvider,
    firstName: true,
    lastName: true,
    phone: true,
  };

  function isFormDirty(data) {
    return (
      data.firstName !== defaultValues.firstName ||
      data.lastName !== defaultValues.lastName ||
      data.phone !== defaultValues.phone ||
      data.birthday !== defaultValues.birthday ||
      data.email !== defaultValues.email
    );
  }

  async function onSubmit(data) {
    setIsLoading(true);
    const originalEmail = user.email;

    try {
      // unenroll users from PBB if the form is dirty and they are enrolled in PBB
      if (isEnrolledDutchiePay && isFormDirty(data)) {
        const { success } = await handleUnenrollPayByBank();
        if (!success) {
          throw new Error(SAVE_ERROR_MESSAGE);
        }
      }
      if (data.email && user.email !== data.email) {
        user.email = data.email;
        // Only require password confirmation if user has a password (not SSO or email code login)
        if (hasPassword) {
          const proceed = await UI.openChangeEmailPasswordPrompt();
          if (!proceed) {
            throw new Error('Email update by password prompt cancelled');
          }
        }
      }

      const { success } = await updateUser({
        profile: {
          ...user?.profile,
          ...data,
        },
      });

      if (!success) {
        throw new Error('Update failed');
      }

      user.refetch();
      showErnie(t('account.updateSuccess', SAVE_SUCCESS_MESSAGE), ERNIE_TYPES.SUCCESS);
    } catch (_error) {
      // eslint-disable-line no-unused-vars
      user.email = originalEmail; // Reset email in case of any failure
      showErnie(t('account.unableToUpdate', SAVE_ERROR_MESSAGE), ERNIE_TYPES.DANGER);
    } finally {
      setIsLoading(false);
    }
  }

  const handleChangePasswordClick = () => {
    openModal(ModalNames.changePassword);
  };

  const handleDeleteAccountClick = async () => {
    await openDeleteAccountModal({
      afterDelete: () => {
        user.logout();
        showErnie(t('ernie.logout', `You've been successfully logged out.`), `success`);
      },
    });
  };

  const handleLogout = () => {
    if (isMobileEcommApp) {
      callNativeMethod(`onAuthChange`, { isLoggedIn: false });
      changeNativeTabToShop();
    }
    user.logout();
    showErnie(t('ernie.logout', `You've been successfully logged out.`), `success`);
  };

  return (
    <Container>
      <TopSection>
        <Heading tag='h1' size='medium'>
          {(props) => props.t('account.myAccount', 'My Account')}
        </Heading>
        {hasPassword && (
          <ChangePassword onClick={handleChangePasswordClick}>
            {t('account.changePassword', 'Change password')}
          </ChangePassword>
        )}
      </TopSection>
      <FormContainer>
        <Form
          onSubmit={onSubmit}
          defaultValues={defaultValues}
          validationSchema={userFormSchema(t, formValidationFields)}
          watch
        >
          <InputRowWrapper>
            <InputContainer mr={13}>
              <FormTextInput
                variant='filled'
                label={t('account.firstName', 'First Name')}
                name={FIELD_NAMES.FIRST_NAME}
              />
            </InputContainer>
            <InputContainer>
              <FormTextInput variant='filled' name={FIELD_NAMES.LAST_NAME} label={t('account.lastName', 'Last Name')} />
            </InputContainer>
          </InputRowWrapper>
          <InputRow>
            <FullWidthInputContainer>
              <FormTextInput
                variant='filled'
                label={t('account.emailAddress', 'Email Address')}
                name={FIELD_NAMES.EMAIL}
                type='email'
                disabled={isLoggedInViaExternalProvider}
                title={
                  isLoggedInViaExternalProvider &&
                  'Email address cannot be changed when logged in with a social account.'
                }
              />
              {isLoggedInViaExternalProvider && (
                <EmailDisabledText size='medium'>
                  Email address cannot be changed when logged in with a social account.
                </EmailDisabledText>
              )}
            </FullWidthInputContainer>
          </InputRow>
          <InputRowWrapper>
            <InputContainer mr={13}>
              <FormFormattedInput
                variant='filled'
                label={t('account.mobilePhone', 'Mobile Phone')}
                name={FIELD_NAMES.PHONE}
                format={FIELD_NAMES.PHONE}
              />
            </InputContainer>
            <InputContainer>
              <FormFormattedInput
                variant='filled'
                label={t('account.birthdate', 'Date of Birth')}
                format={FIELD_NAMES.DATE}
                name={FIELD_NAMES.BIRTHDAY}
              />
            </InputContainer>
          </InputRowWrapper>
          <AccountUpdateButton loading={isLoading} type='submit' data-testid='account-submit-button'>
            {t('common.save', 'Save')}
          </AccountUpdateButton>
          <DeleteAccountBlock>
            <DeleteModalTrigger onClick={handleDeleteAccountClick}>Delete Account</DeleteModalTrigger>
          </DeleteAccountBlock>
          {isMobileEcommApp && (
            <MobileAppSection>
              <HorizontalDivider width='100%' mx='10px' color={colors.grey[90]} />
              <MobileAppActionsSection>
                <LogOutButton onClick={handleLogout}>{t('accountOptions.logout', 'Log Out')}</LogOutButton>
                <DeleteAccountLink />
              </MobileAppActionsSection>
              <HorizontalDivider width='100%' mx='10px' color={colors.grey[90]} />
              <MobileAppInfoSection>
                <MobileEcommTermsLink forAccountPage text={t('common.privacy', 'Privacy & Terms')} />
                <span>{appRevision}</span>
              </MobileAppInfoSection>
            </MobileAppSection>
          )}
        </Form>
      </FormContainer>
    </Container>
  );
}

const Container = styled.div`
  width: 100%;
`;

const FormContainer = styled.div`
  margin: 37px 0 40px;

  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    margin: 24px 0 0 0;
  }
`;

const TopSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const InputContainer = styled.div`
  margin-bottom: 25px;
  width: 50%;
  z-index: 0;
  ${space}
  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    margin-bottom: 22px !important;
    width: 100%;
  }
`;

const FullWidthInputContainer = styled(InputContainer)`
  width: 100%;
`;

const InputRowWrapper = styled(InputRow)`
  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    flex-direction: column;
  }
`;

const AccountUpdateButton = styled(StyledButton)`
  margin-top: 15px;
  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    width: 100%;
    height: 51px;
    font-size: 15px;
  }
`;

const MobileAppFlexSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const MobileAppSection = styled(MobileAppFlexSection)`
  margin-top: 40px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[30]};
`;

const MobileAppActionsSection = styled(MobileAppFlexSection)`
  padding: 40px 0;
  width: 100%;
`;

const MobileAppInfoSection = styled(MobileAppFlexSection)`
  margin-top: 40px;
`;

const LogOutButton = styled(StyledButton)`
  margin: 0 0 26px 0;
  background: ${({ theme }) => theme.colors.blueGrey[80]};
  color: ${({ theme }) => theme.colors.grey[25]};
  width: 100%;
  height: 51px;
  font-size: 15px;
`;

const EmailDisabledText = styled(Typography.Label)`
  color: ${({ theme }) => theme.colors.grey[45]};
  margin-top: 12px;
`;

const DeleteAccountBlock = styled.div`
  width: 100%;
  display: block;
  text-align: center;
  margin: 20px 0 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.values.md}px) {
    width: auto;
    display: inline-block;
    margin: 0;
  }
`;

const DeleteModalTrigger = styled(TextButton)`
  color: var(--dcm-color-primary);
  font-size: 13px;
  line-height: 22px;
  margin-left: 20px;
`;
